import React, { memo, type ReactElement, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { Box } from '@saviynt/design-system';
import cx from 'classnames';

import {
  SodMitigateModal,
  SodReviewModal,
} from '../../../../../shared/components';
import {
  RENDER_MODES,
  VIOLATIONS_INI_CALL,
  violationsHome,
} from '../../../../../shared/constants';
import {
  useInvertedLocalize,
  useTypedSelector,
} from '../../../../../shared/hooks';
import { globalActions } from '../../../../../shared/store/globalSlice';
import testIds from '../../../../../tests/testIds';
import { violationsTabActions } from '../../../../ViolationsTab';
import ViolationsControls from '../../../../ViolationsTab/components/ViolationsControls';
import ViolationsSidePanel from '../../../../ViolationsTab/components/ViolationsSidePanel';
import ViolationsTabFooter from '../../../../ViolationsTab/components/ViolationsTabFooter';
import ViolationsTabGraph from '../../../../ViolationsTab/components/ViolationsTabGraph';
import ViolationsTabList from '../../../../ViolationsTab/components/ViolationsTabList';
import {
  useColumnPicker,
  useFiltersInitialization,
  useGetQueryMap,
  useGetSortMenu,
  usePageTracker,
  useTabFilters,
} from '../../../../ViolationsTab/hooks';

/**
 * ViolationsTab
 *
 * A component that manages and displays violations data in both list and graph modes.
 * It handles the initialization of filters, manages different view modes, and coordinates the display
 * of violations data along with related widgets and side panels.
 *
 * @returns {ReactElement} Returns a section containing violations data visualization
 *
 * @example
 * <ViolationsTab />
 *
 * @dependencies
 * - react
 * - react-redux
 * - @saviynt/design-system
 *
 * @features
 * - Supports both list and graph view modes
 * - Integrates with SodInsights component
 * - Handles advanced and tab filters initialization
 * - Manages side panel visibility
 * - Supports bulk actions with footer
 * - Includes violations widgets in graph mode
 *
 * @state
 * The component relies on the following Redux state:
 * - activeViolationsTab: Current active violations tab
 * - renderMode: Current view mode (list/graph)
 * - isInsightsOpen: Visibility state of insights panel
 * - isSidePanelVisible: Visibility state of side panel
 * - isFooterVisible: Visibility state of footer
 *
 * @hooks
 * - useDispatch: For dispatching Redux actions
 * - useInvertedLocalize: For localization
 * - useTypedSelector: For accessing Redux state
 * - useFiltersInitialization: For initializing advanced filters
 * - useTabFilters: For managing tab filters
 * - usePageTracker: For tracking page state
 * - useGetQueryMap: For handling query parameters
 */

function EntitlementViolationsTab(): ReactElement {
  const dispatch = useDispatch();
  const l = useInvertedLocalize();

  // Initial query hooks
  const queryMap = useGetQueryMap(true);
  const { updateTracker } = usePageTracker(Object.values(VIOLATIONS_INI_CALL));
  const { initializeAdvancedFilters } = useFiltersInitialization(queryMap, l);
  const { initializeTabFilters } = useTabFilters();
  const { initializeViewColumns } = useColumnPicker();

  const {
    aag: {
      global: { activeViolationsTab, isGlobalLoading },
      violationsTab: {
        controls: { renderMode, isInsightsOpen, sortInAction },
        bulkaction: { isFooterVisible },
        sidepanel: { isSidePanelVisible },
        modalIsland: { isMitigateModalOpen, isReviewModalOpen },
      },
    },
  } = useTypedSelector((state) => state);

  // Enable sort hook
  useGetSortMenu(sortInAction);

  useEffect(() => {
    if (activeViolationsTab === violationsHome) {
      const initializeFilters = async () => {
        // Order is important
        const response = await Promise.all([
          initializeAdvancedFilters(),
          initializeTabFilters(),
          initializeViewColumns(),
        ]);

        // Raise global error is any one fails
        const isFalseValue = response.some((value) => !value);

        if (isFalseValue) {
          dispatch(globalActions.setGlobalError({ state: true }));
        }

        // Don't load page if untill all pass
        Object.values(VIOLATIONS_INI_CALL).forEach((callValue, index) => {
          updateTracker(callValue, response[index]);
        });
      };

      initializeFilters().catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeViolationsTab]);

  const isGraphMode = Boolean(renderMode === RENDER_MODES.GRAPH);
  const isListMode = Boolean(renderMode === RENDER_MODES.LIST);
  const renderContent = useMemo(() => {
    if (isGlobalLoading) {
      return null;
    }

    if (isGraphMode) {
      return <ViolationsTabGraph />;
    }

    return (
      <>
        <ViolationsTabList />
        <ViolationsTabFooter />
      </>
    );
  }, [isGraphMode, isGlobalLoading]);

  const violationsTabClasses = useMemo(
    () => cx('Sod-ViolationsTab', isListMode && 'Sod-ViolationsTab--isList'),
    [isListMode]
  );
  const violationsTabViewClasses = useMemo(
    () =>
      cx(
        'Sod-ViolationsTab-view',
        isGraphMode && 'Sod-ViolationsTab-view--isGraph',
        isListMode && 'Sod-ViolationsTab-view--isList',
        isSidePanelVisible && 'Sod-ViolationsTab-view--isSidePanelVisible',
        isInsightsOpen && 'Sod-ViolationsTab-view--isInsightsOpen',
        isFooterVisible && 'Sod-ViolationsTab-view--isFooterVisible',
        !isSidePanelVisible &&
          !isInsightsOpen &&
          'Sod-ViolationsTab-view--isNotAsides'
      ),
    [
      isGraphMode,
      isListMode,
      isSidePanelVisible,
      isInsightsOpen,
      isFooterVisible,
    ]
  );

  return (
    <Box
      tag='section'
      className={violationsTabClasses}
      dataTestId={testIds.SOD_VIOLATIONS_PAGE_BODY}>
      <Box
        className={violationsTabViewClasses}
        dataTheme={isGraphMode ? 'dark' : undefined}>
        <Box className='Sod-ViolationsView-wrapper'>
          <Box className='Sod-ViolationsView-content'>
            <ViolationsControls
              isListMode={isListMode}
              isInsightsEnabled={false}
            />
            {renderContent}
          </Box>

          {isSidePanelVisible && (
            <Box className='Sod-ViolationsView-asides'>
              <ViolationsSidePanel />
            </Box>
          )}
        </Box>
      </Box>
      {/* Modals */}
      <SodMitigateModal
        isOpen={isMitigateModalOpen}
        setIsOpen={() => {
          dispatch(violationsTabActions.setIsMitigateModalOpen(false));
        }}
      />
      <SodReviewModal
        isOpen={isReviewModalOpen}
        setIsOpen={() => {
          dispatch(violationsTabActions.setIsReviewModalOpen(false));
        }}
      />
    </Box>
  );
}

export default memo(EntitlementViolationsTab);
